import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { Layers, ShieldCheck, Sparkles } from 'lucide-react';
export function TechDebt() {
  const pillars = [{
    icon: Layers,
    label: 'Modernize systems'
  }, {
    icon: Sparkles,
    label: 'Improve efficiency'
  }, {
    icon: ShieldCheck,
    label: 'Strengthen security'
  }];
  return <section className="relative py-24 bg-white overflow-hidden" data-id="element-238">
      {/* Background accents */}
      <div className="absolute inset-0 pointer-events-none" data-id="element-239">
        <div className="absolute top-1/3 -left-40 w-96 h-96 rounded-full bg-brand-green/5 blur-3xl" data-id="element-240" />
        <div className="absolute bottom-0 -right-40 w-96 h-96 rounded-full bg-brand-blue/5 blur-3xl" data-id="element-241" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-242">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center" data-id="element-243">
          {/* Image column */}
          <motion.div initial={{
          opacity: 0,
          x: -24
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.6
        }} className="lg:col-span-6 relative" data-id="element-244">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-slate-300/40 aspect-[4/3]" data-id="element-245">
              <img src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1400&q=80" alt="Modern engineering team collaborating" loading="lazy" className="w-full h-full object-cover" data-id="element-246" />
              {/* Subtle gradient overlay for legibility of floating cards */}
              <div className="absolute inset-0 bg-gradient-to-tr from-brand-navy/40 via-transparent to-transparent" data-id="element-247" />
            </div>

            {/* Floating decorative card — stat */}
            <motion.div initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} className="absolute -bottom-6 -right-4 sm:-right-8 bg-white rounded-2xl shadow-xl border border-slate-100 p-5 max-w-[260px]" data-id="element-248">
              <div className="flex items-center gap-3" data-id="element-249">
                <div className="w-12 h-12 rounded-xl bg-brand-green/10 flex items-center justify-center flex-shrink-0" data-id="element-250">
                  <Layers className="w-6 h-6 text-brand-green" strokeWidth={2} data-id="element-251" />
                </div>
                <div data-id="element-252">
                  <p className="text-xs text-slate-500 font-medium" data-id="element-253">
                    Sustainable
                  </p>
                  <p className="text-base font-bold text-slate-900 font-heading leading-tight" data-id="element-254">
                    Long-term Foundations
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Floating decorative card — top left */}
            <motion.div initial={{
            opacity: 0,
            y: -16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.4
          }} className="hidden sm:flex absolute -top-5 -left-5 bg-brand-navy rounded-2xl shadow-xl p-4 items-center gap-2.5" data-id="element-255">
              <span className="flex h-2 w-2 rounded-full bg-brand-green" data-id="element-256" />
              <span className="text-white text-sm font-semibold" data-id="element-257">
                Engineering Confidence
              </span>
            </motion.div>
          </motion.div>

          {/* Text column */}
          <motion.div initial={{
          opacity: 0,
          x: 24
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="lg:col-span-6" data-id="element-258">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-green/10 text-brand-green text-xs font-semibold uppercase tracking-wider mb-5" data-id="element-259">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-green" data-id="element-260" />
              Built For Long-Term Growth
            </span>

            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 font-heading leading-tight mb-6" data-id="element-261">
              Break free from the{' '}
              <span className="text-brand-green" data-id="element-262">tech debt trap</span>
            </h2>

            <div className="space-y-5 text-lg text-slate-600 leading-relaxed mb-8" data-id="element-263">
              <p data-id="element-264">
                Build with structured, maintainable, and scalable software
                foundations built for long-term growth.
              </p>
              <p data-id="element-265">
                BetterCode helps organizations modernize systems, improve
                engineering efficiency, strengthen security, and restore
                confidence in software delivery.
              </p>
            </div>

            {/* Pillar chips */}
            <div className="flex flex-wrap gap-2" data-id="element-266">
              {pillars.map(({
              icon: Icon,
              label
            }) => <span key={label} className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-slate-50 border border-slate-200 text-sm font-medium text-slate-700" data-id="element-267">
                  <Icon className="w-4 h-4 text-brand-green" strokeWidth={2.2} data-id="element-268" />
                  {label}
                </span>)}
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}